import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isYesterday,
  isValid,
} from 'date-fns';

export const getFormattedTimestamp = (lastUpdatedISO: string): string => {
  if (!lastUpdatedISO) {
    // Missing timestamp
    return 'unavailable';
  }

  const lastUpdatedDate = new Date(lastUpdatedISO);

  if (!isValid(lastUpdatedDate)) {
    // Invalid timestamp (unparsable)
    console.error(`Invalid timestamp received: ${lastUpdatedISO}`);
    return 'unavailable';
  }

  const now = new Date();

  const diffInSeconds = differenceInSeconds(now, lastUpdatedDate);

  // Future timestamp
  if (diffInSeconds < 0) {
    if (diffInSeconds < -300) {
      // More than 5 minutes in the future
      console.error(
        `Future timestamp received, treating as invalid: ${lastUpdatedISO}`,
      );
      return 'unavailable';
    }
    // Within 5 minutes in the future
    return 'Just now';
  }

  // Past or present timestamp (diffInSeconds >= 0)
  const timeFormat = format(lastUpdatedDate, 'HH:mm');

  if (diffInSeconds < 60) {
    return `Just now, ${timeFormat}`;
  }

  const diffInMinutes = differenceInMinutes(now, lastUpdatedDate);
  if (diffInMinutes < 60) {
    const relativeTime = `${diffInMinutes} minute${
      diffInMinutes === 1 ? '' : 's'
    } ago`;
    return `${relativeTime}, ${timeFormat}`;
  }

  const diffInHours = differenceInHours(now, lastUpdatedDate);
  if (diffInHours < 24) {
    const relativeTime = `${diffInHours} hour${
      diffInHours === 1 ? '' : 's'
    } ago`;
    return `${relativeTime}, ${timeFormat}`;
  }

  if (isYesterday(lastUpdatedDate)) {
    return `Yesterday, ${timeFormat}`;
  }

  // 2 days ago or more
  return format(lastUpdatedDate, 'D MMMM YYYY, HH:mm');
};
